import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  InputAdornment,
  OutlinedInput,
  LinearProgress,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  TextField,
  Modal,
  Menu,
  MenuItem,
  Popover
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import MicIcon from '@mui/icons-material/Mic';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplyIcon from '@mui/icons-material/Reply';
import { EMOJI_FONT_STACK, TEXT_WITH_EMOJI_FONT_STACK } from '../emojiFont';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import { IconSearch, IconArrowUp, IconArrowDown, IconPlus } from '@tabler/icons';
import {
  useGetConversationsQuery,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useSendMessageMutation,
  useCreateChatRoomMutation,
  useEditMessageMutation,
  useDeleteMessageMutation
} from 'services/chat/chatAPIs';
import { useAppUserActiveClientsListingQuery, useAppUserClientsListingQuery } from 'services/user-management/app-users/appUsersAPIs';
import { useTheme } from '@emotion/react';
import dayjs from 'dayjs';
import { get_user } from '../../../utils/functions';
import { toast } from 'react-toastify';
import { database } from '../../../frebase/firebase';
import { ref as dbRef, set as dbSet, onValue, serverTimestamp } from 'firebase/database';
import ReadReceipt from 'ui-component/chat/ReadReceipt';

// Curated emoji set for the inline picker (no external dependency).
const EMOJIS = [
  '😀','😁','😂','🤣','😊','😍','😘','😎','🤩','🥳','😅','😉','🙂','🙃','😋','😌',
  '🤔','🤨','😐','😴','😭','😤','😡','🥺','😬','🙄','😇','🤗','🤝','🙏','👏','💪',
  '👍','👎','👌','✌️','🤞','👋','🔥','✨','⭐','🎉','💯','✅','❌','⚠️','❤️','🧡',
  '💛','💚','💙','💜','🖤','🏋️','🤸','🏃','🥇','🏆','🎯','💧','🥗','🍎','💤','⏰'
];

function formatRecordTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function TrainerChat() {
  const theme = useTheme();
  const user = get_user();
  const chatContainerRef = useRef(null);

  // State variables
  const [message, setMessage] = useState('');
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const insertEmoji = (emoji) => setMessage((prev) => `${prev}${emoji}`);
  // Typing indicator (Firebase Realtime DB). All RTDB calls are wrapped in
  // try/catch so the chat never breaks if RTDB isn't configured/enabled yet.
  const [otherTyping, setOtherTyping] = useState(false);
  const typingStopTimerRef = useRef(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [senderId, setSenderId] = useState('');
  const [receiverId, setReceiverId] = useState('');
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchChat, setSearchChat] = useState('');
  const [searchMessage, setSearchMessage] = useState('');
  const [chat, setChat] = useState([]);
  const [searchMatches, setSearchMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [searchedChat, setSearchedChat] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  // States for message actions (edit/delete)
  const [messageMenuAnchorEl, setMessageMenuAnchorEl] = useState(null);
  // The message being replied to. Only its _id is sent; the backend resolves the
  // quote on READ (and rejects ids from other rooms), so an edited or deleted
  // original can never leave a stale quote behind.
  const [replyTarget, setReplyTarget] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedMessageContent, setEditedMessageContent] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // New state for user selection modal
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [searchUser, setSearchUser] = useState('');

  // Mutations for creating chat room and sending message
  const [createChatRoom] = useCreateChatRoomMutation();
  const [sendMessage] = useSendMessageMutation();
  const [getUsersChat] = useLazyGetMessagesQuery();
  const [editMessage] = useEditMessageMutation();
  const [deleteMessage] = useDeleteMessageMutation();
  // const [readConversation] = useConversationReadMutation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [localChatsListing, setLocalChatsListing] = useState([]);
  const [clientTab, setClientTab] = useState('active'); // 'active' | 'expired'
  const [convoFilter, setConvoFilter] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingPreviousMessages, setIsLoadingPreviousMessages] = useState(false);
  // We no longer need these states as we're using react-dropzone
  // const [isDraggingOver, setIsDraggingOver] = useState(false);
  // const [draggedFiles, setDraggedFiles] = useState([]);

  // New state for attachment caption
  const [attachmentCaption, setAttachmentCaption] = useState('');

  // Add a new state for tracking message sending
  const [isSending, setIsSending] = useState(false);

  // --- Voice recording (ported from admin-chat so all staff keep the mic) ---
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordReleaseTimerRef = useRef(null);
  const recordRoomRef = useRef(null);
  const recordSenderRef = useRef('');
  const recordReceiverRef = useRef('');
  const MAX_RECORD_SECONDS = 60;

  // Mutations and Queries
  const { data: chatsListing, isLoading } = useGetConversationsQuery(
    {
      from: 1,
      limit: 1500,
      searchChat
    },
    {
      refetchOnMountOrArgChange: true,
      // Keep the conversation list fresh (no realtime layer on the backend).
      pollingInterval: 10000
    }
  );
  useEffect(() => {
    if (chatsListing?.chatRooms) {
      setLocalChatsListing(chatsListing.chatRooms);
    }
  }, [chatsListing]);

  // Active vs expired grouping — dedicated UNFILTERED active-clients list (the
  // other clientsListing query is filtered by the new-chat search, so it can't
  // be reused here). A conversation is Active if its client (userType 3) is in
  // this set, else Expired.
  const { data: allActiveClientsData } = useAppUserActiveClientsListingQuery({ limit: 2000, from: 0, searchName: '' });
  const activeClientIds = useMemo(
    () => new Set((allActiveClientsData?.users || []).map((u) => String(u._id))),
    [allActiveClientsData]
  );
  const clientIdOfRoom = (item) => {
    const parts = item?.chatRoom?.participants || [];
    const c = parts.find((p) => Number(p?.userType) === 3) || parts[0];
    return c?._id ? String(c._id) : '';
  };
  const filteredConvos = useMemo(() => {
    const list = localChatsListing || [];
    const term = convoFilter.trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) =>
      (item?.chatRoom?.participants || []).some(
        (p) => p?.fullName?.toLowerCase().includes(term) || p?.userName?.toLowerCase().includes(term)
      )
    );
  }, [localChatsListing, convoFilter]);
  const byUnread = (arr) => [...arr].sort((a, b) => (b?.unreadCount || 0) - (a?.unreadCount || 0));
  const activeRooms = useMemo(
    () => byUnread(filteredConvos.filter((r) => activeClientIds.has(clientIdOfRoom(r)))),
    [filteredConvos, activeClientIds]
  );
  const expiredRooms = useMemo(
    () => byUnread(filteredConvos.filter((r) => !activeClientIds.has(clientIdOfRoom(r)))),
    [filteredConvos, activeClientIds]
  );
  const visibleRooms = clientTab === 'active' ? activeRooms : expiredRooms;
  const { data: usersChat, isLoading: userChatLoader } = useGetMessagesQuery(
    {
      id: selectedChatId,
      from,
      limit: rowsPerPage,
      searchMessage
    },
    {
      refetchOnMountOrArgChange: true,
      // Poll the open thread every 2s so the other party's replies appear
      // quickly without a manual refresh (no WebSocket/realtime layer).
      pollingInterval: 2000
    }
  );
  const handleImageSelect = (event) => {
    if (!message.trim()) {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = () => {
          setSelectedImage(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Handle chat selection and mark as read
  const handleChatClick = async (item) => {
    try {
      // Check if the same chat is being clicked again
      if (selectedChatId === item?._id) {
        return; // Do nothing if the same chat is clicked again
      }

      // Reset chat states
      setSelectedChat(item);
      setSelectedChatId(item?._id);
      // Drop any pending reply — the backend rejects a quote from another room, so
      // carrying it across would silently send the message without its quote.
      setReplyTarget(null);
      const receiver = item.participants?.find((participant) => participant._id !== user?._id)?._id;
      setSenderId(item?.sender);
      setReceiverId(receiver);

      // Update local chat list to reset unread count
      setLocalChatsListing((prevChats) =>
        prevChats.map((chatRoom) => (chatRoom?.chatRoom?._id === item?._id ? { ...chatRoom, unreadCount: 0 } : chatRoom))
      );

      // Reset all states including attachments
      setSearchMessage('');
      setFrom(1);
      setPage(1);
      setTotalPages(1);
      setHasMoreMessages(true);
      setIsLoadingMoreMessages(false);
      setIsLoadingPreviousMessages(false);
      setChat([]);
      setSearchMatches([]);

      // Clear attachments when switching chats
      clearAttachments();

      // Scroll to bottom of chat
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.log(error, 'error');
    }
  };

  useEffect(() => {
    if (usersChat?.messages) {
      const scrollHeightBeforeUpdate = chatContainerRef.current?.scrollHeight;

      setChat((prevChat) => {
        // Avoid duplicate messages by checking their IDs
        const newChats = usersChat.messages.filter((newMsg) => !prevChat.some((oldMsg) => oldMsg._id === newMsg._id));

        // If loading previous messages, add to beginning, otherwise add to end
        return isLoadingPreviousMessages ? [...newChats, ...prevChat] : [...prevChat, ...newChats];
      });

      // Update total pages
      setTotalPages(usersChat.totalPages || 1);

      // Check if there are more messages to load
      setHasMoreMessages(from < usersChat.totalPages);

      // Maintain scroll position when loading previous messages
      if (isLoadingPreviousMessages && chatContainerRef.current) {
        setTimeout(() => {
          const scrollHeightDifference = chatContainerRef.current.scrollHeight - scrollHeightBeforeUpdate;
          chatContainerRef.current.scrollTop = scrollHeightDifference;
          setIsLoadingMoreMessages(false);
          setIsLoadingPreviousMessages(false);
        }, 100);
      }
    }
  }, [usersChat]);

  // Modify the scroll to bottom useEffect to only trigger for new messages
  useEffect(() => {
    if (chatContainerRef.current && !isLoadingPreviousMessages) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chat]);

  // Add scroll handler for loading previous messages
  const handleChatScroll = async (e) => {
    const { scrollTop } = e.target;

    // Load more messages when scrolled near the top (within 100px)
    if (scrollTop < 100 && hasMoreMessages && !isLoadingMoreMessages && !userChatLoader) {
      setIsLoadingMoreMessages(true);
      setIsLoadingPreviousMessages(true);
      const nextPage = Math.floor(chat.length / rowsPerPage) + 1;
      if (nextPage <= totalPages) {
        setFrom(nextPage);
      }
    }
  };

  const handleSendMessage = async () => {
    try {
      // Use the unified message sending function instead
      sendUnifiedMessage();
    } catch (error) {
      if (Array.isArray(error?.data?.message)) {
        error?.data?.message?.forEach((msg) => {
          toast.error(msg);
        });
      } else {
        toast.error(error?.data?.message);
      }
    }
  };
  const emptySendMessageField = () => {
    setMessage('');
    setSelectedImage(null);
    clearAttachments();
  };

  // --- Typing indicator (Firebase Realtime DB) ---------------------------
  // Coach writes its own typing flag under typing/{roomId}/{coachId}; the
  // client app subscribes to the same node and shows "typing…" for any OTHER
  // uid. The coach's identity MUST be the logged-in admin's own id (user._id),
  // not the room's `sender` field — that field can be the client, which would
  // make the client filter the flag out as its own.
  const writeTyping = (isTyping) => {
    const me = user?._id;
    if (!selectedChatId || !me) return;
    try {
      dbSet(dbRef(database, `typing/${selectedChatId}/${me}`), {
        typing: isTyping,
        at: serverTimestamp()
      });
    } catch (e) {
      // RTDB not configured — ignore.
    }
  };

  const onMessageChange = (e) => {
    const text = e.target.value;
    setMessage(text);
    writeTyping(true);
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(() => writeTyping(false), 3000);
  };

  // Subscribe to the other participant's typing flag for the open room.
  useEffect(() => {
    const me = user?._id;
    if (!selectedChatId || !me) {
      setOtherTyping(false);
      return undefined;
    }
    let unsubscribe = () => {};
    try {
      const node = dbRef(database, `typing/${selectedChatId}`);
      unsubscribe = onValue(node, (snap) => {
        const val = snap.val() || {};
        const now = Date.now();
        const someoneTyping = Object.keys(val).some(
          (uid) => uid !== me && val[uid]?.typing && now - (val[uid]?.at || 0) < 5000
        );
        setOtherTyping(someoneTyping);
      });
    } catch (e) {
      // RTDB not configured — ignore.
    }
    return () => {
      try {
        unsubscribe();
      } catch (e) {
        // ignore
      }
      setOtherTyping(false);
    };
  }, [selectedChatId, user?._id]);

  // Clear our own typing flag + timer on unmount.
  useEffect(
    () => () => {
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      writeTyping(false);
    },
    []
  );
  // Navigate between matches
  // const handleMatchNavigation = (direction) => {
  //   setCurrentMatchIndex((prevIndex) => {
  //     const newIndex = direction === 'up' ? prevIndex - 1 : prevIndex + 1;
  //     if (newIndex < 0) {
  //       return searchMatches?.length - 1;
  //     }
  //     if (newIndex >= searchMatches?.length) {
  //       return 0;
  //     }
  //     return newIndex;
  //   });
  // };
  // const handleMarkAsRead = async (item) => {
  //   const payload = { isRead: true };
  //   if (item?.unreadCount > 0) {
  //     try {
  //       await readConversation({ id: item?._id, payload }).unwrap();
  //       toast.success('Chat is read.');
  //     } catch (error) {
  //       if (Array.isArray(error?.data?.message)) {
  //         error?.data?.message?.forEach((msg) => {
  //           toast.error(msg);
  //         });
  //       } else {
  //         toast.error(error?.data?.message);
  //       }
  //     }
  //   }
  // };
  // Render chat component

  // Add state to track image loading errors
  const [previewLoadError, setPreviewLoadError] = useState(false);

  // Function to handle opening the modal
  const handleOpenModal = (url) => {
    if (!url) {
      console.error('Attempted to open modal with undefined URL');
      // toast.error('Cannot preview attachment: URL is missing');
      return;
    }

    try {
      // Reset error state when opening new preview
      setPreviewLoadError(false);

      // Make sure the URL is valid and accessible
      console.log('Opening preview modal with URL:', url);

      // Don't try to validate blob URLs with fetch - it's unreliable
      // Just set the URLs directly and let the img/video elements handle errors
      setAttachmentUrl(url);
      setPreviewUrl(url);
      setOpenModal(true);
    } catch (error) {
      console.error('Error opening preview modal:', error);
      // toast.error('Failed to open preview. Please try again.');
    }
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    try {
      setOpenModal(false);
      setPreviewLoadError(false);
      // Don't clear preview URLs immediately to avoid flickering
      setTimeout(() => {
        setPreviewUrl('');
        setAttachmentUrl('');
      }, 300);
    } catch (error) {
      console.error('Error closing preview modal:', error);
    }
  };

  // Add clients listing query
  const { data: clientsListing, isLoading: isClientsLoading } = useAppUserActiveClientsListingQuery({
    limit: 1000,
    from: 0,
    searchName: searchUser
  });

  // Method to start a new chat with a client
  const handleStartNewChat = async (client) => {
    try {
      // First, create the chat room or get existing one
      const chatRoomPayload = {
        participants: [user?._id, client._id]
      };

      const chatRoomResponse = await createChatRoom(chatRoomPayload).unwrap();
      const chatRoomId = chatRoomResponse?._id;

      if (!chatRoomId) {
        throw new Error('Failed to create or retrieve chat room');
      }

      // Check if the chat room already has messages
      const hasExistingMessages = chatRoomResponse.messages && chatRoomResponse.messages.length > 0;

      // Create a new chat room object similar to existing chat room structure
      const newChatRoom = {
        _id: chatRoomId,
        participants: [
          { _id: user?._id, fullName: user?.fullName, profileImage: user?.profileImage },
          { _id: client._id, fullName: client.fullName, profileImage: client.profileImage }
        ],
        timestamp: chatRoomResponse.createdAt || new Date().toISOString(),
        messages: chatRoomResponse.messages || []
      };

      // Set the newly created or existing chat room as selected
      handleChatClick(newChatRoom);

      // Close the modal
      handleCloseNewChatModal();
    } catch (error) {
      console.error('Error starting new chat:', error);
      toast.error(error?.data?.message || 'Failed to start new chat. Please try again.');
    }
  };

  // Method to open new chat modal
  const handleOpenNewChatModal = () => {
    setIsNewChatModalOpen(true);
    setSearchUser(''); // Reset search when opening
  };

  // Method to close new chat modal
  const handleCloseNewChatModal = () => {
    setIsNewChatModalOpen(false);
  };

  // Add a utility function to generate unique IDs
  const generateUniqueId = () => {
    return `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Modify the effect that handles paste events to add unique IDs to attachments
  useEffect(() => {
    const handlePaste = async (event) => {
      // Only process if a chat is selected
      if (!selectedChatId) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      const filesToAdd = [];

      // Try to get files from clipboard.files (file manager copy)
      if (clipboardData.files && clipboardData.files.length > 0) {
        for (let i = 0; i < clipboardData.files.length; i++) {
          const file = clipboardData.files[i];

          if (file.size <= 5 * 1024 * 1024) {
            filesToAdd.push(file);
          } else {
            toast.error(`File "${file.name}" is too large (max 5MB)`);
          }
        }
      }

      // Try to get files from clipboard.items (browser copy/screenshot)
      if (filesToAdd.length === 0 && clipboardData.items) {
        for (let i = 0; i < clipboardData.items.length; i++) {
          const item = clipboardData.items[i];

          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file && file.size <= 5 * 1024 * 1024) {
              filesToAdd.push(file);
            }
          }
        }
      }

      // Add files to attachments
      if (filesToAdd.length > 0) {
        const newAttachments = filesToAdd.map((file) => ({
          id: generateUniqueId(),
          file,
          url: URL.createObjectURL(file),
          caption: ''
        }));

        setAttachments((prev) => [...prev, ...newAttachments]);
        event.preventDefault();
      }
    };

    // Add event listener
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [selectedChatId]);

  // Unified send message function that handles text and attachments
  const sendUnifiedMessage = () => {
    // Prevent sending if already sending or no content
    if (isSending || (attachments.length === 0 && !message.trim())) return;

    // Prevent multiple submissions
    setIsSending(true);

    // Clear the composer BEFORE the request. It used to be cleared in .then(), so the
    // text sat in the box for the whole round trip and read as "not sent". Safe to do
    // here: sendSingleMessage below closes over the `message` value from THIS render,
    // so clearing the state does not change what actually gets sent. Restored on failure.
    const pendingText = message;
    const pendingReply = replyTarget;
    setMessage('');
    setReplyTarget(null);

    if (attachments.length > 0 || message.trim()) {
      // If we have both text and attachments, send text with first attachment
      // If we only have text, send text-only message
      // If we only have attachments, send attachments with their captions
      const sendSingleMessage = (index = 0) => {
        const formData = new FormData();

        // Determine content for this message
        if (index === 0 && message.trim()) {
          // First message: use the main text message
          formData.append('content', message.trim());
        } else if (index < attachments.length && attachments[index].caption) {
          // Subsequent messages: use the attachment's caption
          formData.append('content', attachments[index].caption.trim());
        } else if (index === 0 && !message.trim() && attachments.length > 0) {
          // First attachment without main text: use empty string or caption
          formData.append('content', attachments[0].caption || '');
        } else {
          // Default: empty content
          formData.append('content', '');
        }

        // Add the current attachment if available
        if (index < attachments.length) {
          formData.append('attachment', attachments[index].file);
        }

        formData.append('chatRoomId', selectedChatId);
        formData.append('senderId', senderId);
        formData.append('receiverId', receiverId);
        // Quote only the FIRST message of a multi-attachment send — repeating it on
        // every part would render the same quote several times in a row.
        if (index === 0 && pendingReply?._id) {
          formData.append('replyTo', pendingReply._id);
        }

        return sendMessage(formData)
          .unwrap()
          .then((saved) => {
            // Optimistically show the just-sent message immediately. The 2s poll
            // only brings it back when the thread is on page 1; after scrolling
            // up to read history `from` > 1, so the poll fetches an old page and
            // the sender's own message wouldn't appear until re-entering the
            // room. Appending here makes it show instantly (deduped by _id when
            // the poll later returns it). senderDetail isn't populated on the
            // create response, so build it from the logged-in admin.
            if (saved && saved._id) {
              const optimistic = {
                ...saved,
                attachment:
                  saved.attachment && Object.keys(saved.attachment).length
                    ? saved.attachment
                    : null,
                senderDetail: saved.senderDetail || {
                  _id: user?._id,
                  fullName: user?.fullName,
                  profileImage: user?.profileImage,
                },
                // The create response returns replyTo as a bare id; build the quote
                // locally so it renders instantly, in the same shape the read path
                // populates (the poll replaces this object shortly after).
                replyTo:
                  index === 0 && pendingReply?._id
                    ? {
                        _id: pendingReply._id,
                        content: pendingReply.content,
                        attachment: pendingReply.attachment,
                        sender: { fullName: pendingReply?.senderDetail?.fullName },
                      }
                    : null,
              };
              setChat((prev) =>
                prev.some((m) => m._id === optimistic._id) ? prev : [...prev, optimistic],
              );
            }
            // If there are more attachments, send the next one
            if (index + 1 < attachments.length) {
              return sendSingleMessage(index + 1);
            }
            // Otherwise, we're done. The composer was already cleared before the
            // request — clearing again here would wipe anything the coach started
            // typing while this send was in flight.
            clearAttachments();
            setAttachmentCaption('');
            return Promise.resolve();
          })
          .catch((error) => {
            // toast.error(`Failed to send message ${index + 1}`);
            console.error(error);
            return Promise.reject(error);
          });
      };

      // Start sending messages
      sendSingleMessage()
        .then(() => {
          // toast.success('Message(s) sent successfully');

          // Scroll to bottom after sending
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          }, 100);
        })
        .catch((error) => {
          console.error('Error sending messages:', error);
          // Give the coach their text back rather than losing it to a failed send —
          // and the reply target with it, or resending would drop the quote silently.
          setMessage((current) => (current ? current : pendingText));
          setReplyTarget((current) => current || pendingReply);
        })
        .finally(() => {
          // Always reset sending state
          setIsSending(false);
        });
    } else {
      // If no content, immediately reset sending state
      setIsSending(false);
    }
  };

  // --- Voice recording handlers (ported from admin-chat) ---
  const cleanupRecording = () => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordSeconds(0);
    // Keep the mic stream warm briefly so the next recording starts instantly,
    // then release it so the mic indicator doesn't stay on indefinitely.
    if (recordReleaseTimerRef.current) clearTimeout(recordReleaseTimerRef.current);
    recordReleaseTimerRef.current = setTimeout(() => {
      if (recordStreamRef.current) {
        recordStreamRef.current.getTracks().forEach((t) => t.stop());
        recordStreamRef.current = null;
      }
      recordReleaseTimerRef.current = null;
    }, 20000);
  };

  // Record an efficient Opus/WebM voice note (max 1 min) and send as attachment.
  const startRecording = async () => {
    if (!selectedChatId) return;
    recordRoomRef.current = selectedChatId;
    recordSenderRef.current = senderId;
    recordReceiverRef.current = receiverId;
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported in this browser.');
      return;
    }
    try {
      // Reuse the warm mic stream if we still have one so recording starts
      // instantly (the getUserMedia acquisition is what caused the delay).
      if (recordReleaseTimerRef.current) {
        clearTimeout(recordReleaseTimerRef.current);
        recordReleaseTimerRef.current = null;
      }
      let stream = recordStreamRef.current;
      if (!stream || !stream.active) {
        // Audio capture tuning for a one-way voice note:
        // - autoGainControl OFF: AGC starts at full gain then attenuates after
        //   ~1-2s (the "loud then quiet" notes) -> off keeps volume steady.
        // - echoCancellation ON: removes room/speaker echo from the recording.
        // - noiseSuppression ON: cuts background hiss + softens the mouse-click.
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            autoGainControl: false,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        recordStreamRef.current = stream;
      }
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      // Skip the record-button mouse-click: wait briefly so its acoustic tail
      // decays before capture begins. Bail if the user cancelled meanwhile.
      await new Promise((resolve) => setTimeout(resolve, 180));
      if (mediaRecorderRef.current !== recorder) return;
      recorder.start();
      setIsRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSeconds((s) => {
          if (s + 1 >= MAX_RECORD_SECONDS) {
            // Auto-stop and send at the 1-minute cap.
            stopRecordingAndSend();
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error('Microphone permission denied or unavailable.');
      cleanupRecording();
    }
  };

  const cancelRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
    } catch (_) {
      // ignore
    }
    cleanupRecording();
  };

  const stopRecordingAndSend = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    recorder.onstop = async () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const roomId = recordRoomRef.current;
      const sId = recordSenderRef.current;
      const rId = recordReceiverRef.current;
      cleanupRecording();
      if (!blob.size || !roomId) return;
      try {
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('content', '');
        formData.append('attachment', file);
        formData.append('chatRoomId', roomId);
        formData.append('senderId', sId);
        formData.append('receiverId', rId);
        const saved = await sendMessage(formData).unwrap();
        // Optimistically show the sent voice note immediately (see note in
        // sendUnifiedMessage — the poll won't surface it when scrolled up).
        if (saved && saved._id) {
          const optimistic = {
            ...saved,
            senderDetail: saved.senderDetail || {
              _id: user?._id,
              fullName: user?.fullName,
              profileImage: user?.profileImage,
            },
          };
          setChat((prev) =>
            prev.some((m) => m._id === optimistic._id) ? prev : [...prev, optimistic],
          );
        }
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      } catch (e) {
        toast.error(e?.data?.message || e?.message || 'Failed to send voice message');
      }
    };
    try {
      recorder.stop();
    } catch (_) {
      cleanupRecording();
    }
  };

  // Stop the mic / timer if the screen unmounts mid-recording.
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (recordReleaseTimerRef.current) clearTimeout(recordReleaseTimerRef.current);
      if (recordStreamRef.current) recordStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Add ref for stable access to selectedChatId
  const selectedChatIdRef = useRef(selectedChatId);

  // Update ref when selectedChatId changes
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);

  // Helper function to get file icon based on file type/extension
  const getFileIcon = (fileInput, isOwnMessage = false) => {
    if (!fileInput) return <InsertDriveFileIcon sx={{ color: isOwnMessage ? '#fff' : 'text.secondary' }} />;

    // Check if we're dealing with a File object or a URL string
    const isFileObject = fileInput instanceof File;
    const fileType = isFileObject ? fileInput.type : '';
    const fileName = isFileObject ? fileInput.name : String(fileInput);

    // Check MIME type first (for File objects)
    if (isFileObject) {
      if (fileType.startsWith('image/')) {
        return <AddPhotoAlternateIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      } else if (fileType === 'application/pdf') {
        return <PictureAsPdfIcon sx={{ color: isOwnMessage ? '#fff' : 'error.main' }} />;
      } else if (
        fileType === 'application/msword' ||
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        return <DescriptionIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      } else if (
        fileType === 'application/vnd.ms-excel' ||
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        return <TableChartIcon sx={{ color: isOwnMessage ? '#fff' : 'success.main' }} />;
      } else if (fileType.startsWith('video/')) {
        return <VideoFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      } else if (fileType.startsWith('audio/')) {
        return <AudioFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      }
    }

    // Fall back to extension checking for URLs or when MIME type doesn't match
    if (fileName.match(/\.(jpeg|jpg|gif|png)$/i)) {
      return <AddPhotoAlternateIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else if (fileName.match(/\.pdf$/i)) {
      return <PictureAsPdfIcon sx={{ color: isOwnMessage ? '#fff' : 'error.main' }} />;
    } else if (fileName.match(/\.(doc|docx)$/i)) {
      return <DescriptionIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else if (fileName.match(/\.(xls|xlsx)$/i)) {
      return <TableChartIcon sx={{ color: isOwnMessage ? '#fff' : 'success.main' }} />;
    } else if (fileName.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i)) {
      return <VideoFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else if (fileName.match(/\.(mp3|wav|flac|aac|ogg|wma)$/i)) {
      return <AudioFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else {
      return <InsertDriveFileIcon sx={{ color: isOwnMessage ? '#fff' : 'text.secondary' }} />;
    }
  };

  // Add effect to clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all object URLs when component unmounts
      attachments.forEach((attachment) => {
        if (attachment && attachment.url) {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, [attachments]);

  // Update the removeAttachment function to use attachment ID instead of index
  const removeAttachment = (attachmentId) => {
    try {
      // Find the attachment by ID
      const attachmentToRemove = attachments.find((att) => att.id === attachmentId);

      if (!attachmentToRemove) {
        console.error('Attachment not found with ID:', attachmentId);
        return;
      }

      // Store the URL before removing the attachment
      const urlToCheck = attachmentToRemove.url;

      // Safely revoke the URL of the attachment being removed
      if (urlToCheck) {
        try {
          URL.revokeObjectURL(urlToCheck);
        } catch (error) {
          console.error('Error revoking object URL during removal:', error);
        }
      }

      // Create a new array without the removed attachment
      const newAttachments = attachments.filter((att) => att.id !== attachmentId);

      // Update state with the new array
      setAttachments(newAttachments);

      // If we're removing the attachment that's currently being previewed, close the modal
      if (previewUrl === urlToCheck || attachmentUrl === urlToCheck) {
        console.log('Closing modal because the previewed attachment was removed');
        setOpenModal(false);

        // Clear preview URLs after a short delay
        setTimeout(() => {
          setPreviewUrl('');
          setAttachmentUrl('');
        }, 300);
      }
    } catch (error) {
      console.error('Error removing attachment:', error);
      // toast.error('Failed to remove attachment. Please try again.');
    }
  };

  // Update the handleAttachmentCaptionChange function to use attachment ID
  const handleAttachmentCaptionChange = (attachmentId, caption) => {
    try {
      // Find the attachment by ID
      const attachmentIndex = attachments.findIndex((att) => att.id === attachmentId);

      if (attachmentIndex === -1) {
        console.error('Attachment not found with ID:', attachmentId);
        return;
      }

      setAttachments((prevAttachments) => {
        const updatedAttachments = [...prevAttachments];
        updatedAttachments[attachmentIndex] = {
          ...updatedAttachments[attachmentIndex],
          caption: caption || ''
        };
        return updatedAttachments;
      });
    } catch (error) {
      console.error('Error updating attachment caption:', error);
    }
  };

  // Clear all attachments with proper cleanup
  const clearAttachments = useCallback(() => {
    try {
      // Create a copy of the current attachments to avoid mutation issues
      const currentAttachments = [...attachments];

      // Revoke all object URLs to prevent memory leaks
      currentAttachments.forEach((attachment) => {
        if (attachment && attachment.url) {
          try {
            URL.revokeObjectURL(attachment.url);
          } catch (error) {
            console.error('Error revoking object URL:', error);
          }
        }
      });

      // Clear the attachments array
      setAttachments([]);

      // Also clear any preview that might be open
      setPreviewUrl('');
      if (openModal) {
        setOpenModal(false);
      }
    } catch (error) {
      console.error('Error clearing attachments:', error);
      // Fallback to just clearing the array if revocation fails
      setAttachments([]);
    }
  }, [attachments, openModal]);

  // Add a function to safely open attachment previews
  const safelyOpenAttachmentPreview = (attachment) => {
    if (!attachment) {
      console.error('Cannot preview attachment: attachment is null or undefined');
      // toast.error('Cannot preview attachment');
      return;
    }

    if (!attachment.url) {
      console.error('Cannot preview attachment: missing URL');
      // toast.error('Cannot preview attachment: URL is missing');
      return;
    }

    // Log the attachment being previewed to help with debugging
    console.log('Opening preview for attachment:', attachment.id);

    // For blob URLs, we may need to regenerate them if they're invalid
    if (attachment.url.startsWith('blob:') && attachment.file) {
      try {
        // Try to regenerate the URL to ensure it's fresh
        console.log('Regenerating blob URL for preview');

        // Revoke the old URL
        URL.revokeObjectURL(attachment.url);

        // Generate a new URL
        const newUrl = URL.createObjectURL(attachment.file);

        // Update the attachment with the new URL
        setAttachments((prev) => prev.map((att) => (att.id === attachment.id ? { ...att, url: newUrl } : att)));

        // Open the modal with the new URL
        handleOpenModal(newUrl);
      } catch (error) {
        console.error('Error regenerating blob URL:', error);
        // If regeneration fails, try with the original URL as fallback
        handleOpenModal(attachment.url);
      }
    } else {
      // For regular URLs or if regeneration isn't possible, just open the modal
      handleOpenModal(attachment.url);
    }
  };

  // Add a function to format timestamps with date information when needed
  const formatMessageTime = (timestamp) => {
    const messageDate = dayjs(timestamp);
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');

    // If the message is from today, just show the time
    if (messageDate.isSame(today, 'day')) {
      return messageDate.format('h:mm A');
    }
    // If the message is from yesterday, show "Yesterday" and time
    else if (messageDate.isSame(yesterday, 'day')) {
      return `Yesterday, ${messageDate.format('h:mm A')}`;
    }
    // If the message is from this year, show month, day and time
    else if (messageDate.isSame(today, 'year')) {
      return messageDate.format('MMM D, h:mm A');
    }
    // If the message is from a previous year, include the year
    else {
      return messageDate.format('MMM D YYYY, h:mm A');
    }
  };

  // Function to format date for separators
  const formatDateForSeparator = (timestamp) => {
    const messageDate = dayjs(timestamp);
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');

    if (messageDate.isSame(today, 'day')) {
      return 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
      return 'Yesterday';
    } else if (messageDate.isSame(today, 'year')) {
      return messageDate.format('MMMM D');
    } else {
      return messageDate.format('MMMM D, YYYY');
    }
  };

  // Function to check if two dates are different days
  const isDifferentDay = (date1, date2) => {
    if (!date1 || !date2) return true;
    return !dayjs(date1).isSame(dayjs(date2), 'day');
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        outline: 'none' // Remove outline when focused
      }}
    >
      <Grid container sx={{ height: `calc(100vh - 103px)` }}>
        {/* Chat List */}
        <Grid
          item
          lg={3}
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            padding: 2,
            overflowY: 'auto',
            height: '-webkit-fill-available',
            bgcolor: theme.palette.background.paper,
            position: 'sticky',
            top: 0
          }}
        >
          {/* New Chat Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 2
            }}
          >
            <Typography variant="h5">Chats</Typography>
            <Tooltip title="Start New Chat">
              <IconButton
                onClick={handleOpenNewChatModal}
                sx={{
                  bgcolor: 'accent.main',
                  color: '#fff',
                  // Hover was #3050e8 — a blue. The button changed hue entirely on hover
                  // rather than darkening, which read as a different control.
                  '&:hover': {
                    bgcolor: 'secondary.dark'
                  }
                }}
              >
                <IconPlus />
              </IconButton>
            </Tooltip>
          </Box>

          {/* conversation search by client name / username */}
          <OutlinedInput
            sx={{ width: '100%', mb: 1.5 }}
            value={convoFilter}
            onChange={(e) => setConvoFilter(e.target.value)}
            placeholder="Search chats by name or username..."
            endAdornment={
              <InputAdornment position="end">
                <IconSearch stroke={1.5} size="1rem" />
              </InputAdornment>
            }
          />
          {/* Active / Expired client tabs */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            {['active', 'expired'].map((t) => (
              <Box
                key={t}
                onClick={() => setClientTab(t)}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  py: 0.75,
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textTransform: 'capitalize',
                  // These are a segmented control, not two buttons: the unselected half
                  // shouldn't carry the same accent outline as the selected one.
                  color: clientTab === t ? '#fff' : 'text.secondary',
                  border: '1px solid',
                  borderColor: clientTab === t ? 'accent.main' : 'transparent',
                  backgroundColor: clientTab === t ? 'accent.main' : 'transparent',
                  transition: 'background-color .15s ease, color .15s ease',
                  '&:hover': {
                    backgroundColor: clientTab === t ? 'accent.main' : 'surface.hover',
                    color: clientTab === t ? '#fff' : 'text.primary'
                  }
                }}
              >
                {t === 'active' ? `Active (${activeRooms.length})` : `Expired (${expiredRooms.length})`}
              </Box>
            ))}
          </Box>

          {isLoading ? (
            <LinearProgress />
          ) : (
            <Box sx={{ width: '100%', typography: 'body1' }}>
              {visibleRooms?.length > 0 ? (
                visibleRooms.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    padding={1}
                    borderRadius="8px"
                    sx={{
                      // Selected and hover were BOTH solid #673ab7, so moving the mouse
                      // down the list made every row look like the open conversation and
                      // you lost track of which thread you were actually in. Selection now
                      // carries a tinted ground plus an edge; hover is a neutral lift.
                      backgroundColor: selectedChatId === item?.chatRoom?._id ? 'accent.soft' : 'transparent',
                      boxShadow: selectedChatId === item?.chatRoom?._id ? (t) => `inset 2px 0 0 ${t.palette.accent.text}` : 'none',
                      cursor: 'pointer',
                      color: selectedChatId === item?.chatRoom?._id ? 'accent.text' : 'text.primary',
                      transition: 'background-color .15s ease',
                      '&:hover': {
                        backgroundColor: selectedChatId === item?.chatRoom?._id ? 'accent.soft' : 'surface.hover'
                      }
                    }}
                    onClick={() => handleChatClick(item?.chatRoom)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                      {item?.chatRoom?.participants?.map(
                        (participant, index) =>
                          participant._id !== user?._id && (
                            <Box display="flex" alignItems="center" gap={1} key={index} sx={{ minWidth: 0 }}>
                              <img
                                src={participant?.profileImage || '/default-avatar.png'}
                                alt={participant?.fullName}
                                width="35"
                                height="35"
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette.grey[400]
                                }}
                              />
                              <Typography fontWeight={600} sx={{ color: 'inherit' }} noWrap>
                                {participant?.fullName}
                              </Typography>
                            </Box>
                          )
                      )}
                    </Box>
                    {item?.unreadCount > 0 && (
                      <Box
                        sx={{
                          ml: 1,
                          minWidth: 22,
                          height: 22,
                          px: 1,
                          borderRadius: '999px',
                          backgroundColor: 'error.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          // error.main is a light red tuned for dark grounds, so the count
                          // on top of it has to be dark to stay readable.
                          color: 'surface.canvas',
                          fontSize: '0.75rem',
                          fontWeight: 700
                        }}
                      >
                        {item.unreadCount > 99 ? '99+' : item.unreadCount}
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Typography>{convoFilter.trim() ? 'No chats match your search.' : `No ${clientTab} conversations.`}</Typography>
              )}
            </Box>
          )}
        </Grid>

        {/* Chat Messages */}
        {selectedChat ? (
          <Grid
            item
            lg={9}
            sx={{
              overflowY: 'auto',
              position: 'sticky',
              height: 'calc(100vh - 103px)',
              display: 'flex',
              flexDirection: 'column',
              // Recessed canvas against the raised list rail, same as admin-chat.
              bgcolor: 'background.default'
            }}
          >
            {/* Chat Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: 1,
                py: 1,
                px: 2,
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                bgcolor: theme.palette.background.paper
              }}
            >
              {selectedChat?.participants?.map(
                (participant, index) =>
                  participant._id !== user?._id && (
                    <Box display="flex" alignItems="center" gap={1} key={index}>
                      <img
                        src={participant?.profileImage || '/default-avatar.png'}
                        alt={participant?.fullName}
                        width="40"
                        height="40"
                        style={{
                          objectFit: 'cover',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.grey[400]
                        }}
                      />
                      <Box>
                        <Typography fontWeight={600} style={{ color: 'white' }}>
                          {participant?.fullName}
                        </Typography>
                        <Typography style={{ color: 'white' }}>{participant?.userName}</Typography>
                      </Box>
                    </Box>
                  )
              )}

              {/* Help text for file operations */}
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                💡 Copy files (Ctrl+C) and paste (Ctrl+V) to attach files
              </Typography>
            </Box>

            {/* Chat Messages Container */}
            <Box
              ref={chatContainerRef}
              onScroll={handleChatScroll}
              sx={{
                mt: 2,
                flexGrow: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {(userChatLoader || isLoadingMoreMessages) && (
                <Box mx={'auto'} my={1}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {chat?.length > 0 ? (
                chat.map((item, index) => {
                  const isOwnMessage = item?.senderDetail?._id === user?._id;
                  const isPreviousMessageDifferentDay = index > 0 && isDifferentDay(item.timestamp, chat[index - 1].timestamp);

                  return (
                    <React.Fragment key={item?._id}>
                      {isPreviousMessageDifferentDay && (
                        <Box
                          sx={{
                            textAlign: 'center',
                            width: '100%',
                            my: 2,
                            position: 'relative'
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.5,
                              borderRadius: '10px',
                              backgroundColor: 'rgba(103, 58, 183, 0.2)',
                              color: '#fff'
                            }}
                          >
                            {formatDateForSeparator(item.timestamp)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                          width: '100%',
                          marginBottom: 2
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            padding: 2,
                            borderRadius: '12px',
                            // The incoming bubble was theme.palette.grey[200] — which this
                            // theme never defines, so it fell through to MUI's #eeeeee and
                            // put a near-white block in the middle of a dark panel. Both
                            // bubbles now sit on the surface ladder: the client's on the
                            // hover rung, yours on the accent.
                            backgroundColor: isOwnMessage ? 'accent.main' : 'surface.hover',
                            color: 'text.primary'
                          }}
                        >
                          <Box display="flex" gap={2}>
                            {!isOwnMessage ? (
                              <img
                                src={item?.senderDetail?.profileImage || '/default-avatar.png'}
                                alt={item?.senderDetail?.fullName}
                                width="40"
                                height="40"
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette.grey[400]
                                }}
                              />
                            ) : (
                              // Coach/own messages show the CalisFix logo
                              <img
                                src="/calisfix-logo.png"
                                alt="CalisFix"
                                width="40"
                                height="40"
                                style={{
                                  objectFit: 'contain',
                                  borderRadius: '50%',
                                  backgroundColor: '#fff',
                                  padding: 2
                                }}
                              />
                            )}
                            <Box sx={{ width: '100%' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography fontWeight={600} color={'text.primary'}>
                                  {item?.senderDetail?.fullName}
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      fontSize: '0.85rem',
                                      fontWeight: 400,
                                      color: 'rgba(255,255,255,0.72)'
                                    }}
                                  >
                                    {formatMessageTime(item?.timestamp)}
                                  </Typography>
                                  {/* Only on our own messages: a tick on the client's
                                      message would report whether WE read it. */}
                                  {isOwnMessage && <ReadReceipt isRead={item?.isRead} readAt={item?.readAt} />}
                                </Typography>

                                {/* On EVERY message, not just own ones — Reply is the whole
                                    point of having it on a client's message. The menu itself
                                    hides Edit/Delete for messages that aren't ours. */}
                                <IconButton
                                  size="small"
                                  title="Message actions"
                                  sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    '&:hover': { color: 'text.primary' }
                                  }}
                                  onClick={(event) => {
                                    setMessageMenuAnchorEl(event.currentTarget);
                                    setSelectedMessage(item);
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {isEditMode && selectedMessage?._id === item._id ? (
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  value={editedMessageContent}
                                  onChange={(e) => setEditedMessageContent(e.target.value)}
                                  multiline
                                  size="small"
                                  autoFocus
                                  sx={{
                                    mt: 1,
                                    '& .MuiOutlinedInput-root': {
                                      color: 'text.primary',
                                      '& fieldset': {
                                        borderColor: 'rgba(255,255,255,0.45)'
                                      },
                                      '&:hover fieldset': {
                                        borderColor: 'text.primary'
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: 'text.primary'
                                      }
                                    }
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleSaveEdit()}
                                          sx={{ color: isOwnMessage ? '#fff' : 'primary.main' }}
                                        >
                                          <IconArrowUp />
                                        </IconButton>
                                      </InputAdornment>
                                    )
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSaveEdit();
                                    } else if (e.key === 'Escape') {
                                      setIsEditMode(false);
                                    }
                                  }}
                                />
                              ) : (
                                <>
                                  {/* Quoted message — resolved by the API on read, so it
                                      always reflects the original's current text. */}
                                  {item?.replyTo && (
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        mb: 0.75,
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        // Quoted reply: the left rule is the blockquote
                                        // affordance every messaging product uses, kept
                                        // deliberately. The incoming ground was 6% black,
                                        // which was near-invisible once the incoming
                                        // bubble stopped being light grey.
                                        borderLeft: '3px solid',
                                        borderColor: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'accent.text',
                                        backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.25)'
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: '0.78rem',
                                          fontWeight: 700,
                                          color: 'rgba(255,255,255,0.9)'
                                        }}
                                      >
                                        {item?.replyTo?.sender?.fullName || 'Message'}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: '0.82rem',
                                          fontFamily: TEXT_WITH_EMOJI_FONT_STACK,
                                          // Both bubbles are dark now (accent purple / surface.hover),
                                          // so the quote body is light on either side. The old
                                          // near-black incoming value was left over from the
                                          // light-grey bubble and rendered ~1.3:1 — unreadable.
                                          color: 'rgba(255,255,255,0.85)',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden'
                                        }}
                                      >
                                        {item?.replyTo?.content || (item?.replyTo?.attachment ? 'Attachment' : '')}
                                      </Typography>
                                    </Box>
                                  )}
                                  {item?.attachment ? (
                                    <Box>
                                      {item?.attachment?.fileType?.startsWith('audio/') ? (
                                        // Audio / voice message
                                        <audio
                                          src={item?.attachment?.url}
                                          controls
                                          style={{ marginTop: '10px', maxWidth: '240px' }}
                                        />
                                      ) : item?.attachment?.url && item?.attachment?.url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                                        // Image attachment
                                        <img
                                          src={item?.attachment?.url}
                                          alt="user-image"
                                          width="100"
                                          height="100"
                                          style={{
                                            objectFit: 'cover',
                                            marginTop: '10px',
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => handleOpenModal(item?.attachment?.url)}
                                        />
                                      ) : item?.attachment?.url && item?.attachment?.url.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i) ? (
                                        // Video attachment
                                        <video
                                          src={item?.attachment?.url}
                                          width="200"
                                          height="150"
                                          controls
                                          style={{
                                            marginTop: '10px',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => handleOpenModal(item?.attachment?.url)}
                                        />
                                      ) : (
                                        // Non-image/video attachment
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            marginTop: '10px',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                            cursor: 'pointer',
                                            maxWidth: '200px'
                                          }}
                                          onClick={() => {
                                            // For non-image/video files, we open in a new tab or download
                                            window.open(item?.attachment?.url, '_blank');
                                          }}
                                        >
                                          {getFileIcon(item?.attachment?.url, isOwnMessage)}
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: 'text.primary',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {item?.attachment?.originalname || 'File'}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Typography
                                        variant="body2"
                                        dir="auto"
                                        sx={{
                                          mt: 0.5,
                                          color: 'text.primary',
                                          fontFamily: TEXT_WITH_EMOJI_FONT_STACK
                                        }}
                                      >
                                        {item.content}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      dir="auto"
                                      sx={{
                                        mt: 0.5,
                                        color: 'text.primary',
                                        fontFamily: TEXT_WITH_EMOJI_FONT_STACK
                                      }}
                                    >
                                      {item.content}
                                      {item.isEdited && (
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: '0.75rem',
                                            ml: 1,
                                            color: isOwnMessage ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                                            fontStyle: 'italic'
                                          }}
                                        >
                                          (edited)
                                        </Typography>
                                      )}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })
              ) : (
                <Typography padding={3} textAlign="center">
                  No messages found.
                </Typography>
              )}
            </Box>

            {/* Message Input Field */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: 1,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                position: 'sticky',
                bottom: 0
              }}
            >
              {/* Attachment Preview Area */}
              {attachments.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(0,0,0,0.05)'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {attachments.length} file(s) ready to send. Add text below and send together:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1
                    }}
                  >
                    {attachments.map((attachment, index) => {
                      // Skip rendering if attachment or file is undefined/null
                      if (!attachment || !attachment.file) {
                        console.error('Invalid attachment at index', index);
                        return null;
                      }

                      return (
                        <Box
                          key={attachment.id}
                          sx={{
                            position: 'relative',
                            width: 80,
                            height: 80
                          }}
                        >
                          {attachment.file.type && attachment.file.type.startsWith('image/') ? (
                            // Image preview for image files
                            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                              <img
                                src={attachment.url}
                                alt={`Attachment ${index}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '4px'
                                }}
                                onClick={() => safelyOpenAttachmentPreview(attachment)}
                                onError={(e) => {
                                  console.error('Error loading image preview in chat:', e);

                                  // Try to regenerate the URL if the file is still available
                                  if (attachment.file) {
                                    try {
                                      // Revoke the old URL
                                      URL.revokeObjectURL(attachment.url);

                                      // Generate a new URL
                                      const newUrl = URL.createObjectURL(attachment.file);

                                      // Update the attachment with the new URL
                                      setAttachments((prev) =>
                                        prev.map((att) => (att.id === attachment.id ? { ...att, url: newUrl } : att))
                                      );

                                      // Update the image source
                                      e.target.src = newUrl;
                                    } catch (regenerateError) {
                                      console.error('Failed to regenerate URL:', regenerateError);
                                      e.target.src = '/default-avatar.png'; // Fallback image
                                    }
                                  } else {
                                    e.target.src = '/default-avatar.png'; // Fallback image
                                  }
                                }}
                              />
                            </Box>
                          ) : attachment.file.type && attachment.file.type.startsWith('video/') ? (
                            // Video preview for video files
                            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                              <video
                                src={attachment.url}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                                onClick={() => safelyOpenAttachmentPreview(attachment)}
                                onMouseEnter={(e) => {
                                  try {
                                    e.target.play();
                                  } catch (error) {
                                    console.error('Error playing video preview:', error);
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  try {
                                    e.target.pause();
                                    e.target.currentTime = 0;
                                  } catch (error) {
                                    console.error('Error pausing video preview:', error);
                                  }
                                }}
                                onError={(e) => {
                                  console.error('Error loading video preview in chat:', e);

                                  // Try to regenerate the URL if the file is still available
                                  if (attachment.file) {
                                    try {
                                      // Revoke the old URL
                                      URL.revokeObjectURL(attachment.url);

                                      // Generate a new URL
                                      const newUrl = URL.createObjectURL(attachment.file);

                                      // Update the attachment with the new URL
                                      setAttachments((prev) =>
                                        prev.map((att) => (att.id === attachment.id ? { ...att, url: newUrl } : att))
                                      );

                                      // Update the video source
                                      e.target.src = newUrl;
                                    } catch (regenerateError) {
                                      console.error('Failed to regenerate URL for video:', regenerateError);
                                      // No good fallback for videos, so just show an error message
                                      const parent = e.target.parentNode;
                                      if (parent) {
                                        const errorDiv = document.createElement('div');
                                        errorDiv.textContent = 'Video preview unavailable';
                                        errorDiv.style.width = '100%';
                                        errorDiv.style.height = '100%';
                                        errorDiv.style.display = 'flex';
                                        errorDiv.style.alignItems = 'center';
                                        errorDiv.style.justifyContent = 'center';
                                        errorDiv.style.backgroundColor = '#f0f0f0';
                                        errorDiv.style.borderRadius = '4px';
                                        parent.replaceChild(errorDiv, e.target);
                                      }
                                    }
                                  }
                                }}
                                muted
                              />
                            </Box>
                          ) : (
                            // Icon preview for other file types
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px',
                                backgroundColor: '#f5f5f5',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                try {
                                  // For non-image/video files, download them
                                  const link = document.createElement('a');
                                  link.href = attachment.url;
                                  link.download = attachment.file.name || 'file';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                } catch (error) {
                                  console.error('Error downloading file:', error);
                                  // toast.error('Failed to download file. Please try again.');
                                }
                              }}
                            >
                              {getFileIcon(attachment.file)}
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '9px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  width: '100%',
                                  textAlign: 'center'
                                }}
                              >
                                {attachment.file.name && attachment.file.name.length > 10
                                  ? `${attachment.file.name.substring(0, 7)}...`
                                  : attachment.file.name || 'File'}
                              </Typography>
                            </Box>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeAttachment(attachment.id)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'background.paper',
                              boxShadow: 1,
                              '&:hover': {
                                bgcolor: 'error.light',
                                color: 'white'
                              }
                            }}
                          >
                            ✕
                          </IconButton>
                          {/* Optional caption input for each attachment */}
                          <TextField
                            size="small"
                            placeholder="Caption"
                            value={attachment.caption || ''}
                            onChange={(e) => handleAttachmentCaptionChange(attachment.id, e.target.value)}
                            sx={{
                              position: 'absolute',
                              bottom: -30,
                              left: 0,
                              width: '100%',
                              '& .MuiInputBase-input': {
                                py: 0.5,
                                px: 1,
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Typing indicator */}
              {otherTyping && (
                <Typography sx={{ px: 2, py: 0.5, fontSize: 12, fontStyle: 'italic', color: theme.palette.grey[500] }}>
                  typing…
                </Typography>
              )}

              {/* Replying-to preview — sits directly above the composer so it is
                  obvious what the next message will quote, and cancellable. */}
              {replyTarget && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(255,255,255,0.08)'
                  }}
                >
                  <ReplyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
                      Replying to {replyTarget?.senderDetail?.fullName || 'message'}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '0.82rem',
                        fontFamily: TEXT_WITH_EMOJI_FONT_STACK,
                        color: 'rgba(255,255,255,0.7)'
                      }}
                    >
                      {replyTarget?.content || (replyTarget?.attachment ? 'Attachment' : '')}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setReplyTarget(null)} sx={{ color: '#fff' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Input Field */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isRecording ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', color: '#fff' }}>
                    <IconButton color="error" onClick={cancelRecording} title="Cancel">
                      <CloseIcon />
                    </IconButton>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 5,
                        bgcolor: 'rgba(244, 67, 54, 0.12)'
                      }}
                    >
                      <FiberManualRecordIcon
                        sx={{
                          color: 'error.main',
                          fontSize: 14,
                          animation: 'cf-rec-pulse 1.2s ease-in-out infinite',
                          '@keyframes cf-rec-pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.25 }
                          }
                        }}
                      />
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {formatRecordTime(recordSeconds)} / 1:00
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>Recording…</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton color="primary" onClick={stopRecordingAndSend} title="Send voice message">
                      {isSending ? <CircularProgress size={24} /> : <IconArrowUp />}
                    </IconButton>
                  </Box>
                ) : (
                <OutlinedInput
                  // Emoji fonts at the tail of the stack so emoji size with the text
                  // instead of taking whatever fallback the browser picks.
                  sx={{
                    flexGrow: 1,
                    fontFamily: TEXT_WITH_EMOJI_FONT_STACK,
                    fontSize: '1rem',
                    '& textarea, & input': { fontSize: '1rem', lineHeight: 1.5 }
                  }}
                  id="input-message"
                  value={message}
                  onChange={onMessageChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && (message.trim() || attachments.length > 0)) {
                      sendUnifiedMessage();
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    // Let it bubble up to the global handler
                  }}
                  placeholder="Type your message..."
                  multiline
                  maxRows={4}
                  startAdornment={
                    <InputAdornment position="start">
                      <input
                        type="file"
                        accept="*/*"
                        multiple
                        style={{ display: 'none' }}
                        id="upload-image"
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          if (files.length > 0) {
                            const validFiles = files.filter((file) => {
                              const isValidSize = file.size <= 5 * 1024 * 1024;
                              if (!isValidSize) {
                                toast.error('File size should not exceed 5MB');
                              }
                              return isValidSize;
                            });

                            const newAttachments = validFiles.map((file) => ({
                              id: generateUniqueId(),
                              file,
                              url: URL.createObjectURL(file),
                              caption: ''
                            }));

                            setAttachments((prev) => [...prev, ...newAttachments]);
                          }
                          // Clear the input so the same file can be selected again
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor="upload-image">
                        <IconButton component="span">
                          <AddPhotoAlternateIcon />
                        </IconButton>
                      </label>
                      <IconButton onClick={(e) => setEmojiAnchor(e.currentTarget)} title="Emoji">
                        <EmojiEmotionsIcon />
                      </IconButton>
                      <IconButton onClick={startRecording} disabled={isSending} title="Record voice message">
                        <MicIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={sendUnifiedMessage}
                        disabled={isSending || (!message.trim() && attachments.length === 0)}
                        color="primary"
                      >
                        {isSending ? <CircularProgress size={24} /> : <IconArrowUp />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                )}
                <Popover
                  open={Boolean(emojiAnchor)}
                  anchorEl={emojiAnchor}
                  onClose={() => setEmojiAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  {/* Same fix as admin-chat: emoji are text and were inheriting
                      IconButton's grey plus the app's emoji-less font stack. */}
                  <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.25, maxWidth: 320 }}>
                    {EMOJIS.map((emoji) => (
                      <IconButton
                        key={emoji}
                        size="small"
                        onClick={() => insertEmoji(emoji)}
                        sx={{
                          fontSize: '1.1rem',
                          lineHeight: 1,
                          color: 'initial',
                          opacity: 1,
                          fontFamily: EMOJI_FONT_STACK
                        }}
                      >
                        {emoji}
                      </IconButton>
                    ))}
                  </Box>
                </Popover>
              </Box>
            </Box>
          </Grid>
        ) : (
          <Grid
            item
            lg={9}
            sx={{
              overflowY: 'auto',
              position: 'sticky',
              height: 'calc(100vh - 103px)',
              display: 'flex',
              flexDirection: 'column',
              // Recessed canvas against the raised list rail, same as admin-chat.
              bgcolor: 'background.default'
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography variant="h4">Start a New Chat</Typography>
            </Box>

            {isClientsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                {clientsListing?.users?.length > 0 ? (
                  <Grid container spacing={2}>
                    {clientsListing.users.map((client) => (
                      <Grid item xs={12} sm={6} md={4} key={client._id}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          padding={2}
                          borderRadius="8px"
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'accent.main',
                              color: '#fff'
                            }
                          }}
                          onClick={() => handleStartNewChat(client)}
                        >
                          <img
                            src={client?.profileImage || '/default-avatar.png'}
                            alt={client?.fullName}
                            width="50"
                            height="50"
                            style={{
                              objectFit: 'cover',
                              borderRadius: '50%',
                              backgroundColor: theme.palette.grey[400]
                            }}
                          />
                          <Box>
                            <Typography fontWeight={600} style={{ color: 'white' }}>
                              {client?.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {client?.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography textAlign="center" color="text.secondary">
                    No clients found.
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
        )}
      </Grid>
      {/* Modal to display the attachment */}
      {openModal && (
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="lg"
          sx={{
            '& .MuiDialog-paper': {
              minHeight: { xs: '60vh', sm: '70vh', md: '80vh' },
              maxHeight: { xs: '90vh', sm: '85vh', md: '80vh' },
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          fullWidth
          aria-labelledby="attachment-preview-title"
          disableEscapeKeyDown
        >
          <DialogTitle id="attachment-preview-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{previewLoadError ? 'Preview Error' : 'Attachment Preview'}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              ✕
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', padding: 0 }}>
            {previewLoadError ? (
              // Show error message when preview fails to load
              <Box sx={{ textAlign: 'center', p: 3, width: '100%' }}>
                <Typography variant="h6" color="error" gutterBottom>
                  Unable to load preview
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  The file may have been modified or removed.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    // Try regenerating the preview
                    if (previewUrl.startsWith('blob:')) {
                      // For attachments in the current chat
                      const matchingAttachment = attachments.find((att) => att.url === previewUrl);
                      if (matchingAttachment && matchingAttachment.file) {
                        safelyOpenAttachmentPreview(matchingAttachment);
                      }
                    }
                  }}
                >
                  Try Again
                </Button>
              </Box>
            ) : previewUrl || attachmentUrl ? (
              (previewUrl || attachmentUrl).match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i) ? (
                // Video preview
                <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <video
                    key={`video-${previewUrl || attachmentUrl}`}
                    src={previewUrl || attachmentUrl}
                    controls
                    style={{ width: '100%', height: 'auto', maxHeight: '70vh' }}
                    autoPlay
                    onError={(e) => {
                      console.error('Error loading video in preview modal:', e);
                      console.error('Video source:', previewUrl || attachmentUrl);
                      setPreviewLoadError(true);
                      // toast.error('Failed to load video preview');
                    }}
                  />
                </Box>
              ) : (
                // Image preview with fallback
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative'
                  }}
                >
                  <img
                    key={`img-${previewUrl || attachmentUrl}`}
                    src={previewUrl || attachmentUrl}
                    alt="Attachment"
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    onError={(e) => {
                      console.error('Error loading image in preview modal:', e);
                      console.error('Image source:', previewUrl || attachmentUrl);
                      setPreviewLoadError(true);
                      // toast.error('Failed to load image preview');
                    }}
                  />
                </Box>
              )
            ) : (
              // Fallback when URL is missing
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  No preview available
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* New Chat Modal */}
      <Dialog open={isNewChatModalOpen} onClose={handleCloseNewChatModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Start a New Chat</Typography>
            <OutlinedInput
              sx={{ width: '50%' }}
              placeholder="Search users..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <IconSearch stroke={1.5} size="1rem" />
                </InputAdornment>
              }
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {isClientsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {clientsListing?.users
                ?.filter(
                  (client) =>
                    !searchUser ||
                    client.fullName.toLowerCase().includes(searchUser.toLowerCase()) ||
                    client.email.toLowerCase().includes(searchUser.toLowerCase())
                )
                .map((client) => (
                  <Grid item xs={12} sm={6} md={4} key={client._id}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      padding={2}
                      borderRadius="8px"
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          backgroundColor: 'accent.main',
                          color: '#fff',
                          transform: 'scale(1.02)'
                        }
                      }}
                      onClick={() => handleStartNewChat(client)}
                    >
                      <img
                        src={client?.profileImage || '/default-avatar.png'}
                        alt={client?.fullName}
                        width="50"
                        height="50"
                        style={{
                          objectFit: 'cover',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.grey[400]
                        }}
                      />
                      <Box>
                        <Typography fontWeight={600} style={{ color: 'white' }}>
                          {client?.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {client?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              {clientsListing?.users?.filter(
                (client) =>
                  !searchUser ||
                  client.fullName.toLowerCase().includes(searchUser.toLowerCase()) ||
                  client.email.toLowerCase().includes(searchUser.toLowerCase())
              ).length === 0 && (
                <Grid item xs={12}>
                  <Typography textAlign="center" color="text.secondary" padding={3}>
                    No users found matching your search.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Actions Menu */}
      <Menu
        anchorEl={messageMenuAnchorEl}
        open={Boolean(messageMenuAnchorEl)}
        onClose={() => setMessageMenuAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem
          onClick={() => {
            setReplyTarget(selectedMessage);
            setMessageMenuAnchorEl(null);
            document.getElementById('input-message')?.focus();
          }}
        >
          <ReplyIcon fontSize="small" sx={{ mr: 1 }} />
          Reply
        </MenuItem>
        {/* Editing or deleting someone else's message is not ours to do. */}
        {selectedMessage?.senderDetail?._id === user?._id && [
          <MenuItem
            key="edit"
            onClick={() => {
              setIsEditMode(true);
              setEditedMessageContent(selectedMessage?.content || '');
              setMessageMenuAnchorEl(null);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              setShowDeleteConfirmation(true);
              setMessageMenuAnchorEl(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        ]}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmation} onClose={() => setShowDeleteConfirmation(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography textAlign="center" color="text.secondary">
            Are you sure you want to delete this message? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmation(false)} color="secondary" variant="contained">
            Cancel
          </Button>
          <Button onClick={handleDeleteMessage} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );

  // Function to handle editing a message
  function handleSaveEdit() {
    if (!editedMessageContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    if (selectedMessage && editedMessageContent !== selectedMessage.content) {
      editMessage({
        messageId: selectedMessage._id,
        content: editedMessageContent.trim()
      })
        .unwrap()
        .then(() => {
          // Update the message in the local state to avoid refetching
          setChat((prevChat) =>
            prevChat.map((msg) =>
              msg._id === selectedMessage._id ? { ...msg, content: editedMessageContent.trim(), isEdited: true } : msg
            )
          );
          // toast.success('Message updated successfully');
        })
        .catch((error) => {
          console.log('error', error?.data?.message);
          // toast.error(error?.data?.message || 'Failed to update message');
        })
        .finally(() => {
          setIsEditMode(false);
          setSelectedMessage(null);
          setEditedMessageContent('');
        });
    } else {
      setIsEditMode(false);
    }
  }

  // Function to handle deleting a message
  function handleDeleteMessage() {
    if (selectedMessage) {
      deleteMessage(selectedMessage._id)
        .unwrap()
        .then(() => {
          // Remove the message from the local state
          setChat((prevChat) => prevChat.filter((msg) => msg._id !== selectedMessage._id));
          // toast.success('Message deleted successfully');
        })
        .catch((error) => {
          console.log('error', error?.data?.message);
          // toast.error(error?.data?.message || 'Failed to delete message');
        })
        .finally(() => {
          setShowDeleteConfirmation(false);
          setSelectedMessage(null);
        });
    }
  }
}

export default TrainerChat;
